import React, { useRef, useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import { ChatMessage as ChatMessageType, ChatAction } from '../../../types/chat';
import { LeadGenProgress, LEAD_TABLE_STATUS } from '../../../types';
import { CHAT_TEXT } from '../../../constants/chat';
import { ChatMessagesArea } from './ChatMessagesArea';
import { ChatInputArea } from './ChatInputArea';
import { ChatOverflowMenu, MenuActions, QuickAction, LeadProgressIndicator, EditableCriteriaSection } from '../shared';
import { ChatLoadingIndicator } from '../ChatLoadingIndicator';
import { useClickOutside } from '../../../hooks/chat';

// Flow component imports
import { CampaignCreationFlow } from '../CampaignCreationFlow';
import { MonitorSetupFlow } from '../MonitorSetupFlow';
import { EnrichmentFlow } from '../EnrichmentFlow';
import { FindMoreFlow } from '../FindMoreFlow';
import type { CampaignCompletionSummary } from '../campaign/types';

interface TableContext {
  name?: string;
  type?: 'companies' | 'people' | 'custom';
  columns?: { key: string; name: string; isSegment?: boolean }[];
  status?: string;
  [key: string]: any;
}

interface CampaignContext {
  totalLeadCount: number;
  filteredLeadCount?: number;
  selectedLeadCount?: number;
  selectedLeadIds?: string[];
  hasActiveFilters?: boolean;
  rows?: Array<{ id: string; data: Record<string, any>; [key: string]: any }>;
}

interface EnrichmentContext {
  rowCount: number;
  columns: string[];
  preSelectedColumns?: string[];
  triggeredFromChat?: boolean;
}

interface FindMoreContext {
  currentCount: number;
  initialCount?: number;
  triggeredFromChat?: boolean;
}

interface ChatPanelEmbeddedProps {
  // Core state
  tableId?: string;
  tableContext?: TableContext;
  messages: ChatMessageType[];
  isLoading: boolean;
  isLoadingHistory: boolean;
  inputMessage: string;
  onInputChange: (value: string) => void;
  onSend: () => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  onExecuteAction: (action: ChatAction) => void;

  // Refs
  panelInputRef: React.RefObject<HTMLTextAreaElement>;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  adjustTextareaHeight: (textarea: HTMLTextAreaElement | null) => void;

  // Header/Menu
  hideHeader?: boolean;
  menuActions: MenuActions;

  // Quick actions
  quickActions: QuickAction[];

  // Progress data
  progressData?: LeadGenProgress;

  // Editable criteria
  editableCriteriaProps?: {
    criteria: string[];
    editingIndex: number | null;
    editingValue: string;
    hasEdited: boolean;
    isCreating: boolean;
    editInputRef: React.RefObject<HTMLTextAreaElement>;
    onEdit: (index: number) => void;
    onSave: () => void;
    onCancel: () => void;
    onRemove: (index: number) => void;
    onRun: () => void;
    onEditingValueChange: (value: string) => void;
  };

  // Flow states
  showCampaignCreation?: boolean;
  campaignContext?: CampaignContext;
  onCampaignCreated?: (campaignId: string, summary?: CampaignCompletionSummary) => void;
  onCampaignCancel?: () => void;
  onCampaignEnrichData?: (missingColumns: string[]) => void;
  onEnterRowSelectionMode?: () => void;
  onExitRowSelectionMode?: () => void;
  onSelectAllRows?: () => void;
  onSelectFirstNRows?: (n: number) => void;

  showMonitorSetup?: boolean;
  onMonitorSetupComplete?: (keepMessages?: boolean) => void;
  onMonitorSetupCancel?: () => void;

  showEnrichmentFlow?: boolean;
  enrichmentContext?: EnrichmentContext;
  onEnrichmentComplete?: (keepMessages?: boolean) => void;
  onEnrichmentCancel?: () => void;
  onEnrichmentStarted?: (columnName: string) => void;
  onAddFlowMessages?: (messages: Array<{ id: string; role: 'user' | 'assistant'; content: string; timestamp: Date }>) => Promise<void>;

  showFindMoreFlow?: boolean;
  findMoreContext?: FindMoreContext;
  onFindMoreComplete?: (keepMessages?: boolean) => void;
  onFindMoreCancel?: () => void;
  onFindMoreStarted?: (count: number) => void;
}

/**
 * Embedded chat panel - renders inline in parent container
 * Handles all flow components and message display
 */
export const ChatPanelEmbedded: React.FC<ChatPanelEmbeddedProps> = ({
  tableId,
  tableContext,
  messages,
  isLoading,
  isLoadingHistory,
  inputMessage,
  onInputChange,
  onSend,
  onKeyPress,
  onExecuteAction,
  panelInputRef,
  messagesEndRef,
  adjustTextareaHeight,
  hideHeader = false,
  menuActions,
  quickActions,
  progressData,
  editableCriteriaProps,
  // Campaign flow
  showCampaignCreation,
  campaignContext,
  onCampaignCreated,
  onCampaignCancel,
  onCampaignEnrichData,
  onEnterRowSelectionMode,
  onExitRowSelectionMode,
  onSelectAllRows,
  onSelectFirstNRows,
  // Monitor flow
  showMonitorSetup,
  onMonitorSetupComplete,
  onMonitorSetupCancel,
  // Enrichment flow
  showEnrichmentFlow,
  enrichmentContext,
  onEnrichmentStarted,
  onAddFlowMessages,
  // Find more flow
  showFindMoreFlow,
  findMoreContext,
  onFindMoreStarted,
}) => {
  const overflowMenuRef = useRef<HTMLDivElement>(null);
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);

  useClickOutside(overflowMenuRef, () => setShowOverflowMenu(false), showOverflowMenu);

  // Determine entity type for progress indicator
  const entityType = tableContext?.type === 'people' ? 'people' : tableContext?.type === 'custom' ? 'results' : 'companies';

  // Determine if any flow is active
  const hasActiveFlow = showCampaignCreation || showMonitorSetup;
  const hasInlineFlow = showEnrichmentFlow || showFindMoreFlow;

  // Scroll to bottom when any flow becomes active
  useEffect(() => {
    if (hasActiveFlow || hasInlineFlow) {
      // Delay must exceed the chat panel width transition (280ms) so the
      // scroll container has its final dimensions before scrollIntoView fires.
      const timer = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [hasActiveFlow, hasInlineFlow, messagesEndRef]);

  // Render flow component
  const renderFlowComponent = () => {
    if (showCampaignCreation && tableId && tableContext?.name && campaignContext) {
      return (
        <CampaignCreationFlow
          tableId={tableId}
          tableName={tableContext.name}
          columns={tableContext.columns || []}
          rows={campaignContext.rows}
          totalLeadCount={campaignContext.totalLeadCount}
          filteredLeadCount={campaignContext.filteredLeadCount}
          selectedLeadCount={campaignContext.selectedLeadCount}
          selectedLeadIds={campaignContext.selectedLeadIds}
          hasActiveFilters={campaignContext.hasActiveFilters}
          onComplete={(campaignId, summary) => onCampaignCreated?.(campaignId, summary)}
          onCancel={() => onCampaignCancel?.()}
          onEnterRowSelectionMode={onEnterRowSelectionMode}
          onExitRowSelectionMode={onExitRowSelectionMode}
          onSelectAllRows={onSelectAllRows}
          onSelectFirstNRows={onSelectFirstNRows}
          onEnrichData={onCampaignEnrichData}
          onAddMessages={onAddFlowMessages}
        />
      );
    }

    if (showMonitorSetup && tableId && tableContext?.name) {
      return (
        <MonitorSetupFlow
          tableId={tableId}
          tableName={tableContext.name}
          onComplete={(keepMessages) => onMonitorSetupComplete?.(keepMessages)}
          onCancel={() => onMonitorSetupCancel?.()}
        />
      );
    }

    return null;
  };

  // Render inline flow (enrichment/find more) after messages
  const renderInlineFlow = () => {
    if (showEnrichmentFlow && tableId && enrichmentContext) {
      return (
        <EnrichmentFlow
          tableId={tableId}
          rowCount={enrichmentContext.rowCount}
          columns={enrichmentContext.columns}
          preSelectedColumns={enrichmentContext.preSelectedColumns}
          triggeredFromChat={enrichmentContext.triggeredFromChat}
          onEnrichmentStarted={onEnrichmentStarted}
          onAddMessages={onAddFlowMessages}
        />
      );
    }

    if (showFindMoreFlow && tableId && findMoreContext) {
      return (
        <FindMoreFlow
          tableId={tableId}
          currentCount={findMoreContext.currentCount}
          initialCount={findMoreContext.initialCount}
          triggeredFromChat={findMoreContext.triggeredFromChat}
          onFindMoreStarted={onFindMoreStarted}
          onAddMessages={onAddFlowMessages}
        />
      );
    }

    return null;
  };

  // Render progress indicator
  const renderProgressIndicator = () => {
    if (progressData && progressData.completion < 100 && tableContext?.status === LEAD_TABLE_STATUS.GENERATING) {
      return (
        <LeadProgressIndicator
          progressData={progressData}
          entityType={entityType}
        />
      );
    }
    return null;
  };

  // Render editable criteria section
  const renderEditableCriteria = () => {
    if (tableContext?.status === LEAD_TABLE_STATUS.COMPLETED_NO_RESULTS && editableCriteriaProps && editableCriteriaProps.criteria.length > 0) {
      return (
        <EditableCriteriaSection
          criteria={editableCriteriaProps.criteria}
          editingIndex={editableCriteriaProps.editingIndex}
          editingValue={editableCriteriaProps.editingValue}
          hasEdited={editableCriteriaProps.hasEdited}
          isCreating={editableCriteriaProps.isCreating}
          editInputRef={editableCriteriaProps.editInputRef}
          onEdit={editableCriteriaProps.onEdit}
          onSave={editableCriteriaProps.onSave}
          onCancel={editableCriteriaProps.onCancel}
          onRemove={editableCriteriaProps.onRemove}
          onRun={editableCriteriaProps.onRun}
          onEditingValueChange={editableCriteriaProps.onEditingValueChange}
        />
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Panel Header - only show if not hidden by parent */}
      {!hideHeader && (
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-white flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-gray-900 rounded-full flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </div>
            <div>
              <h3 className="text-[13px] font-semibold text-gray-900">{CHAT_TEXT.BRAND_NAME}</h3>
            </div>
          </div>

          {/* Overflow Menu */}
          <ChatOverflowMenu
            ref={overflowMenuRef}
            isOpen={showOverflowMenu}
            onToggle={() => setShowOverflowMenu(!showOverflowMenu)}
            onClose={() => setShowOverflowMenu(false)}
            actions={menuActions}
            position="below"
            buttonSize="sm"
          />
        </div>
      )}

      {/* Messages Area - always show messages, with flows rendered inline after messages */}
      {/* Extra bottom padding on mobile (pb-32) to account for fixed input area */}
      <div className={`flex-1 bg-gray-50/30 overflow-y-auto`}>
        <div className="p-4 pb-32 lg:pb-4 space-y-3">
          <ChatMessagesArea
            messages={messages}
            isLoading={isLoading && !hasActiveFlow && !hasInlineFlow}
            isLoadingHistory={isLoadingHistory}
            onExecuteAction={onExecuteAction}
            messagesEndRef={!hasActiveFlow && !hasInlineFlow ? messagesEndRef : undefined}
            progressIndicator={!hasActiveFlow && !hasInlineFlow ? renderProgressIndicator() : null}
            editableCriteriaSection={!hasActiveFlow ? renderEditableCriteria() : null}
            variant="embedded"
            className=""
          />

          {/* Inline flows (enrichment, find more) render after messages */}
          {renderInlineFlow()}

          {/* Progress indicator - render after inline flows so it appears at bottom */}
          {hasInlineFlow && renderProgressIndicator()}

          {/* Active flows (email, monitor, campaign) render after messages */}
          {hasActiveFlow && (
            <div className={showCampaignCreation ? '-mx-4' : ''}>
              {renderFlowComponent()}
            </div>
          )}

          {/* Loading indicator when flow is active */}
          {isLoading && (hasActiveFlow || hasInlineFlow) && (
            <ChatLoadingIndicator />
          )}

          {/* Scroll anchor - always at the bottom */}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area - always visible for all flows */}
      <ChatInputArea
        inputMessage={inputMessage}
        onInputChange={onInputChange}
        onSend={onSend}
        onKeyPress={onKeyPress}
        isLoading={isLoading}
        inputRef={panelInputRef}
        adjustTextareaHeight={adjustTextareaHeight}
        quickActions={quickActions}
        variant="embedded"
      />
    </div>
  );
};
